import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Edit3, Eye, Save, RotateCcw, Sparkles, Loader2, ChevronDown } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface SubjectSuggestion {
  subject: string;
  style: string;
  explanation: string;
  length: number;
}

interface EmailContentEditorProps {
  initialContent: {
    subject: string;
    html_content: string;
    variables_used: string[];
  };
  onSave: (content: { subject: string; html_content: string; variables_used: string[] }) => void;
  onCancel: () => void;
  contentType?: string;
  tone?: string;
  keyPoints?: string[];
}

export const EmailContentEditor: React.FC<EmailContentEditorProps> = ({
  initialContent,
  onSave,
  onCancel,
  contentType = 'announcement',
  tone = 'professional',
  keyPoints = []
}) => {
  const [subject, setSubject] = useState(initialContent.subject);
  const [htmlContent, setHtmlContent] = useState(initialContent.html_content);
  const [activeTab, setActiveTab] = useState('edit');
  const [hasChanges, setHasChanges] = useState(false);
  
  // AI Subject Suggestions
  const [subjectSuggestions, setSubjectSuggestions] = useState<SubjectSuggestion[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    const hasSubjectChanged = subject !== initialContent.subject;
    const hasContentChanged = htmlContent !== initialContent.html_content;
    setHasChanges(hasSubjectChanged || hasContentChanged);
  }, [subject, htmlContent, initialContent]);

  const handleSave = () => {
    onSave({
      subject,
      html_content: htmlContent,
      variables_used: initialContent.variables_used
    });
  };

  const handleReset = () => {
    setSubject(initialContent.subject);
    setHtmlContent(initialContent.html_content);
  };

  // Extract text content from HTML for text editing
  const extractTextContent = (html: string) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  // Convert text back to simple HTML
  const convertTextToHtml = (text: string) => {
    return text
      .split('\n\n')
      .map(paragraph => paragraph.trim())
      .filter(paragraph => paragraph.length > 0)
      .map(paragraph => `<p>${paragraph.replace(/\n/g, '<br>')}</p>`)
      .join('\n');
  };

  const [textContent, setTextContent] = useState(extractTextContent(htmlContent));

  const handleTextContentChange = (value: string) => {
    setTextContent(value);
    setHtmlContent(convertTextToHtml(value));
  };

  const generateSubjectSuggestions = async () => {
    setLoadingSuggestions(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-subject-suggestions', {
        body: {
          content: htmlContent || textContent,
          content_type: contentType,
          tone: tone,
          key_points: keyPoints,
          current_subject: subject,
          count: 6
        }
      });

      if (error) throw error;

      if (data.success) {
        setSubjectSuggestions(data.suggestions);
        setShowSuggestions(true);
        toast.success(`Generated ${data.suggestions.length} subject suggestions!`);
      } else {
        throw new Error(data.error || 'Failed to generate suggestions');
      }
    } catch (error: any) {
      console.error('Error generating subject suggestions:', error);
      toast.error('Failed to generate suggestions: ' + error.message);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const selectSuggestion = (suggestion: SubjectSuggestion) => {
    setSubject(suggestion.subject);
    setShowSuggestions(false);
    toast.success('Subject updated!');
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Edit3 className="w-5 h-5" />
            Edit Email Content
          </CardTitle>
          <div className="flex items-center gap-2">
            {hasChanges && (
              <Badge variant="secondary">Unsaved changes</Badge>
            )}
            {initialContent.variables_used.length > 0 && (
              <Badge variant="outline">
                {initialContent.variables_used.length} variables
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Subject Line Editor */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="subject">Email Subject</Label>
            <Popover open={showSuggestions} onOpenChange={setShowSuggestions}>
              <PopoverTrigger asChild>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={generateSubjectSuggestions}
                  disabled={loadingSuggestions}
                  className="flex items-center gap-2"
                >
                  {loadingSuggestions ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      AI Suggest
                    </>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0" align="end">
                <div className="p-4 border-b">
                  <h4 className="font-medium text-sm flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Subject Suggestions
                  </h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    Click any suggestion to use it
                  </p>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {subjectSuggestions.length > 0 ? (
                    <div className="p-2 space-y-1">
                      {subjectSuggestions.map((suggestion, index) => (
                        <button
                          key={index}
                          onClick={() => selectSuggestion(suggestion)}
                          className="w-full text-left p-3 rounded hover:bg-muted transition-colors"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <p className="font-medium text-sm">{suggestion.subject}</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {suggestion.explanation}
                              </p>
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              <Badge variant="outline" className="text-xs">
                                {suggestion.style}
                              </Badge>
                              <span className={`text-xs ${
                                suggestion.length <= 50 ? 'text-green-600' : 'text-orange-600'
                              }`}>
                                {suggestion.length} chars
                              </span>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 text-center text-sm text-muted-foreground">
                      {loadingSuggestions ? 'Generating suggestions...' : 'Click "AI Suggest" to generate ideas'}
                    </div>
                  )}
                </div>
                {subjectSuggestions.length > 0 && (
                  <div className="p-2 border-t">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={generateSubjectSuggestions}
                      disabled={loadingSuggestions}
                      className="w-full"
                    >
                      Generate More
                    </Button>
                  </div>
                )}
              </PopoverContent>
            </Popover>
          </div>
          <Input
            id="subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Enter email subject..."
            className="text-base"
          />
          <p className="text-xs text-muted-foreground">
            Character count: {subject.length} (recommended: under 50)
          </p>
        </div>

        <Separator />

        {/* Content Editor Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="edit">Text Editor</TabsTrigger>
            <TabsTrigger value="html">HTML Editor</TabsTrigger>
            <TabsTrigger value="preview">Preview</TabsTrigger>
          </TabsList>

          <TabsContent value="edit" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="textContent">Email Content (Text)</Label>
              <Textarea
                id="textContent"
                value={textContent}
                onChange={(e) => handleTextContentChange(e.target.value)}
                placeholder="Enter your email content here..."
                className="min-h-[300px] font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Tip: Use double line breaks for new paragraphs. HTML will be generated automatically.
              </p>
            </div>
          </TabsContent>

          <TabsContent value="html" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="htmlContent">Email Content (HTML)</Label>
              <Textarea
                id="htmlContent"
                value={htmlContent}
                onChange={(e) => setHtmlContent(e.target.value)}
                placeholder="Enter HTML content..."
                className="min-h-[300px] font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Advanced: Edit the HTML directly for full control over styling and structure.
              </p>
            </div>
          </TabsContent>

          <TabsContent value="preview" className="space-y-4">
            <div className="space-y-2">
              <Label>Email Preview</Label>
              <div className="border rounded-lg p-6 bg-white min-h-[300px] max-h-96 overflow-y-auto">
                <div className="border-b pb-4 mb-4">
                  <h3 className="font-semibold text-lg">Subject: {subject}</h3>
                </div>
                <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Variables Used */}
        {initialContent.variables_used.length > 0 && (
          <div className="space-y-2">
            <Label>Template Variables Used</Label>
            <div className="flex flex-wrap gap-2">
              {initialContent.variables_used.map((variable) => (
                <Badge key={variable} variant="outline" className="text-xs">
                  {variable}
                </Badge>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              These variables will be automatically replaced when sending emails.
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between items-center pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleReset}
            disabled={!hasChanges}
            className="flex items-center gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Reset Changes
          </Button>
          
          <div className="flex gap-3">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              disabled={!subject.trim() || !htmlContent.trim()}
              className="flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              Save Changes
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};